import React, { useState, useEffect, useRef, useCallback } from 'react';
import MonitoringSettingsModal from './MonitoringSettingsModal';
import MonitoringReport from './MonitoringReport';
import IntelligenceReport from './IntelligenceReport';
import EntityDetailPanel from '../EntityDetailPanel';
import MonitoringOverview from './monitoring/MonitoringOverview';
import MonitoringReportTab from './monitoring/MonitoringReportTab';
import { buildPeriods, useMonitoringReport, AI_CATEGORY_LABELS } from './monitoring/reportData';

// Lazy: the movements view carries the map boundaries + d3, so it loads as
// its own chunk only when the Movements tab is opened (keeps the main bundle
// at its pre-map size).
const MonitoringMovements = React.lazy(() => import('./monitoring/MonitoringMovements'));

interface MonitoringStatus {
  mode: string;
  projectMonitoringEnabled: boolean;
  candidatesDue: number;
  totalMonitored: number;
  totalCandidatesInProject: number;
  recentExecutions: Array<{
    status: string;
    count: string;
    total_changes: string;
  }>;
  crossProjectMonitored: number;
  crossProjectSources: Array<{
    id: number;
    name: string;
    candidate_count: string;
  }>;
}

interface BDInsights {
  summary: {
    totalCompaniesTracked: number;
    totalHiring: number;
    totalAttrition: number;
    netMovement: number;
    expandingCompanies: number;
    contractingCompanies: number;
    newCompaniesDetected: number;
  };
  companyTrends: Array<{
    company_name: string;
    candidates_joined: number;
    candidates_left: number;
    net_movement: number;
    hiring_trend: string;
  }>;
  recentMovements: Array<{
    candidate_name: string;
    from_company_name: string;
    to_company_name: string;
    from_position: string;
    to_position: string;
    movement_type: string;
    detected_at: string;
  }>;
  topHiring: Array<{
    company_name: string;
    candidates_joined: number;
    net_movement: number;
  }>;
  topAttrition: Array<{
    company_name: string;
    candidates_left: number;
    net_movement: number;
  }>;
  newCompanies: Array<{
    company_name: string;
    candidate_count: string;
    first_detected: string;
  }>;
  jobChangeDetections: Array<{
    company_name: string;
    hire_count: number;
    latest_move?: string;
    hires: Array<{
      candidateId: number;
      candidateName: string;
      position: string;
      linkedinUrl: string;
      detectedAt: string;
      startedOn?: string | null;
    }>;
  }>;
  backfillDetections: Array<{
    company_name: string;
    departure_count: number;
    latest_move?: string;
    departures: Array<{
      candidateId: number;
      candidateName: string;
      position: string;
      linkedinUrl: string;
      detectedAt: string;
      leftOn?: string | null;
      movedTo: string;
    }>;
  }>;
}

interface UsageStats {
  hasSubscription: boolean;
  subscription?: {
    planId: string;
    planName: string;
    includedChecks: number;
    creditsUsedThisCycle: number;
    remainingThisCycle: number;
    usagePercentage: number;
    currentPeriodStart: string;
    currentPeriodEnd: string;
  };
  today?: {
    checksPerformed: number;
    changesDetected: number;
    jobChanges: number;
    dailyQuota: number;
    remaining: number;
    usagePercentage: number;
  };
  thisWeek?: {
    checksPerformed: number;
    changesDetected: number;
    jobChanges: number;
  };
  thisMonth?: {
    checksPerformed: number;
    changesDetected: number;
    jobChanges: number;
  };
}

interface MonitoringDashboardProps {
  projectId: number;
  projectName: string;
  focusAlertId?: number | null;
  onFocusAlertConsumed?: () => void;
}

const MonitoringDashboard: React.FC<MonitoringDashboardProps> = ({
  projectId,
  projectName,
  focusAlertId,
  onFocusAlertConsumed
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'candidate' | 'movements' | 'bd' | 'report'>('overview');

  // Period selection shared by the Overview / Movements / Report tabs. One
  // report fetch per (project, period) powers all three.
  const [periods] = useState(() => buildPeriods());
  const [periodKey, setPeriodKey] = useState(() => buildPeriods()[0].key);
  const activePeriod = periods.find(p => p.key === periodKey) || periods[0];
  const {
    data: reportData,
    loading: reportLoading,
  } = useMonitoringReport(projectId, activePeriod.from, activePeriod.to);
  const [status, setStatus] = useState<MonitoringStatus | null>(null);
  const [bdInsights, setBdInsights] = useState<BDInsights | null>(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastRunResult, setLastRunResult] = useState<any>(null);
  const [initializing, setInitializing] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showIntelReport, setShowIntelReport] = useState(false);
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);

  // Persistent alerts state (Candidate Signals)
  const [alerts, setAlerts] = useState<any[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertFilters, setAlertFilters] = useState({
    status: 'unread', // 'unread', 'all', 'read', 'completed', 'snoozed'
    priority: 'all', // 'all', 'critical', 'high', 'medium', 'low'
    alertType: 'all' // 'all', 'high_risk_candidate', 'open_to_work', etc.
  });

  // Persisted per-type filter for Candidate Signals — types the user has
  // chosen to hide (e.g. Open to Work). Saved in localStorage so it doesn't
  // reset. Applied client-side on top of the fetched alerts.
  const HIDDEN_SIGNAL_TYPES_KEY = 'mm_monitoring_hidden_signal_types';
  const [hiddenSignalTypes, setHiddenSignalTypes] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(HIDDEN_SIGNAL_TYPES_KEY);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  });
  const toggleHiddenSignalType = (type: string) => {
    setHiddenSignalTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type); else next.add(type);
      try { localStorage.setItem(HIDDEN_SIGNAL_TYPES_KEY, JSON.stringify(Array.from(next))); } catch { /* ignore */ }
      return next;
    });
  };

  // Inline company + contacts context per alert (item 3): expand a signal to
  // see the company's mapped data and any mapped hiring managers so it can be
  // actioned without leaving the dashboard.
  const [companyCtxOpen, setCompanyCtxOpen] = useState<Set<number>>(new Set());
  const [companyCtx, setCompanyCtx] = useState<Record<number, { loading: boolean; company: any; contacts: any[]; error?: boolean }>>({});
  const toggleCompanyContext = (alertId: number, companyName: string) => {
    const isOpen = companyCtxOpen.has(alertId);
    setCompanyCtxOpen(prev => {
      const next = new Set(prev);
      if (isOpen) next.delete(alertId); else next.add(alertId);
      return next;
    });
    if (isOpen || companyCtx[alertId] || !companyName) return;
    setCompanyCtx(prev => ({ ...prev, [alertId]: { loading: true, company: null, contacts: [] } }));
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/monitoring/company-context?projectId=${projectId}&company=${encodeURIComponent(companyName)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setCompanyCtx(prev => ({ ...prev, [alertId]: { loading: false, company: data.company, contacts: data.contacts || [] } }));
        } else {
          setCompanyCtx(prev => ({ ...prev, [alertId]: { loading: false, company: null, contacts: [], error: true } }));
        }
      } catch {
        setCompanyCtx(prev => ({ ...prev, [alertId]: { loading: false, company: null, contacts: [], error: true } }));
      }
    })();
  };

  // Mapped contacts for the BD detection cards, keyed by company name. Loaded
  // lazily when a card is expanded (reuses the /company-context endpoint).
  const [bdContacts, setBdContacts] = useState<Record<string, { loading: boolean; company: any; contacts: any[]; error?: boolean }>>({});
  // Opens the full contact panel (same one the data table uses) so the client
  // can enrich email/phone and reach out without leaving the dashboard.
  const [contactPanel, setContactPanel] = useState<{ open: boolean; entityId: number | null; entityType: 'contact' | 'candidate' }>({ open: false, entityId: null, entityType: 'contact' });
  const loadBdContacts = (companyName: string) => {
    if (!companyName || bdContacts[companyName]) return;
    setBdContacts(prev => ({ ...prev, [companyName]: { loading: true, company: null, contacts: [] } }));
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/monitoring/company-context?projectId=${projectId}&company=${encodeURIComponent(companyName)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setBdContacts(prev => ({ ...prev, [companyName]: { loading: false, company: data.company, contacts: data.contacts || [] } }));
        } else {
          setBdContacts(prev => ({ ...prev, [companyName]: { loading: false, company: null, contacts: [], error: true } }));
        }
      } catch {
        setBdContacts(prev => ({ ...prev, [companyName]: { loading: false, company: null, contacts: [], error: true } }));
      }
    })();
  };

  // Renders the mapped hiring-manager contacts for a BD detection card.
  const renderBdContacts = (companyName: string) => {
    const ctx = bdContacts[companyName];
    return (
      <div className="mt-3 pt-3 border-t border-gray-200">
        <h5 className="text-sm font-semibold text-gray-700 mb-2">Mapped contacts at {companyName}</h5>
        {!ctx || ctx.loading ? (
          <p className="text-xs text-gray-500">Loading contacts…</p>
        ) : ctx.contacts && ctx.contacts.length > 0 ? (
          <ul className="space-y-1.5">
            {ctx.contacts.map((c: any) => (
              <li key={c.id} className="flex items-center justify-between gap-2 text-xs border-b border-gray-100 pb-1.5 last:border-0">
                <button
                  onClick={() => setContactPanel({ open: true, entityId: c.id, entityType: 'contact' })}
                  className="truncate text-left min-w-0 group"
                  title="Open contact — enrich email/phone & view profile"
                >
                  <span className="font-medium text-gray-800 group-hover:text-blue-600 group-hover:underline">{c.name}</span>
                  {c.job_title && <span className="text-gray-500"> · {c.job_title}</span>}
                </button>
                <span className="flex items-center gap-2.5 whitespace-nowrap">
                  {c.email && <a href={`mailto:${c.email}`} className="text-gray-500 hover:text-blue-600" title={c.email}>✉️</a>}
                  {c.phone && <a href={`tel:${c.phone}`} className="text-gray-500 hover:text-blue-600" title={c.phone}>📞</a>}
                  {c.li_profile_url && (
                    <a href={c.li_profile_url} target="_blank" rel="noreferrer" className="text-[#0077B5] hover:opacity-80" title="LinkedIn profile">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                    </a>
                  )}
                  <button
                    onClick={() => setContactPanel({ open: true, entityId: c.id, entityType: 'contact' })}
                    className="px-2 py-0.5 rounded-md bg-violet-600 text-white text-[11px] font-medium hover:bg-violet-700 transition-colors"
                    title="Find email & phone and view full profile"
                  >
                    Enrich
                  </button>
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-gray-500">No mapped contacts for this company yet. Use “Find Contacts” on the company to add some.</p>
        )}
      </div>
    );
  };

  // Deep-link focus: when arriving from an email alert link, open Candidate
  // Signals and widen the status filter so the alert shows even if it's read/
  // completed.
  useEffect(() => {
    if (!focusAlertId) return;
    setActiveTab('candidate');
    setAlertFilters(prev => (prev.status === 'all' ? prev : { ...prev, status: 'all' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusAlertId]);

  // Once the focused alert is in the list, scroll to it + highlight, then
  // release the focus (so the glow fades and refreshes don't re-scroll).
  useEffect(() => {
    if (!focusAlertId || alerts.length === 0) return;
    const el = document.getElementById(`mon-alert-${focusAlertId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const t = setTimeout(() => onFocusAlertConsumed?.(), 5000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusAlertId, alerts]);

  // Alert detail modal state
  const [selectedAlert, setSelectedAlert] = useState<any | null>(null);
  const [showAlertDetailModal, setShowAlertDetailModal] = useState(false);

  // Market events (cohort corridors: acquisitions, lift-outs, rebrands) —
  // shown at the top of BD Intelligence.
  const [cohortAlerts, setCohortAlerts] = useState<any[]>([]);
  const loadCohortAlerts = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `/api/monitoring/alerts?projectId=${projectId}&alertType=cohort_movement&status=all&limit=10`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setCohortAlerts((data.alerts || []).filter((a: any) => a.status !== 'dismissed'));
      }
    } catch { /* non-fatal */ }
  };
  useEffect(() => {
    if (activeTab === 'bd') loadCohortAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // lemlist push (candidate-directed signal outreach)
  const [lemlistConnected, setLemlistConnected] = useState(false);
  const [lemlistCampaigns, setLemlistCampaigns] = useState<any[]>([]);
  const [showLemlistPush, setShowLemlistPush] = useState(false);
  const [lemlistPushAlert, setLemlistPushAlert] = useState<any | null>(null);
  const [selectedPushCampaignId, setSelectedPushCampaignId] = useState('');
  const [lemlistPushing, setLemlistPushing] = useState(false);

  // Check lemlist connection + load campaigns once, so the push action can show.
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/lemlist/status', { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        setLemlistConnected(data.connected);
        if (data.connected) {
          const cRes = await fetch('/api/lemlist/campaigns', { headers: { Authorization: `Bearer ${token}` } });
          if (cRes.ok) {
            const cData = await cRes.json();
            setLemlistCampaigns(Array.isArray(cData) ? cData : []);
          }
        }
      } catch (e) {
        // Silently ignore — the push button simply won't render.
      }
    })();
  }, []);

  const openLemlistPush = (alertRow: any) => {
    setLemlistPushAlert(alertRow);
    setSelectedPushCampaignId('');
    setShowLemlistPush(true);
  };

  const pushSignalToLemlist = async () => {
    if (!lemlistPushAlert || !selectedPushCampaignId) return;
    setLemlistPushing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/lemlist/send-signal', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ alertId: lemlistPushAlert.id, campaignId: selectedPushCampaignId }),
      });
      const data = await res.json();
      if (res.ok) {
        alert(data.message || 'Pushed to lemlist');
        setShowLemlistPush(false);
        setShowAlertDetailModal(false);
      } else {
        alert(data.error || 'Failed to push to lemlist');
      }
    } catch (e) {
      alert('Failed to push to lemlist');
    } finally {
      setLemlistPushing(false);
    }
  };

  // BD alerts state
  const [bdAlerts, setBdAlerts] = useState<any[]>([]);
  // Job changes that started 4+ months before we spotted them: hidden by
  // default (the server counts them), shown on request.
  const [includeOldMoves, setIncludeOldMoves] = useState(false);
  const [olderMoves, setOlderMoves] = useState(0);
  const [bdAlertsLoading, setBdAlertsLoading] = useState(false);
  const [bdAlertFilters, setBdAlertFilters] = useState({
    status: 'unread',
    priority: 'all',
    alertType: 'all' // 'all', 'company_hiring_trend', 'company_movement', 'new_hire_detected'
  });

  // BD company expansion state (for Job Changes and Backfill sections)
  const [expandedCompanies, setExpandedCompanies] = useState<Set<string>>(new Set());
  const [completedCompanies, setCompletedCompanies] = useState<Set<string>>(new Set());
  // BD detection lists: most people first, or most recent move first (date
  // they started / left, from their career — not when monitoring noticed).
  const [bdSort, setBdSort] = useState<'count' | 'recent'>('count');

  // Track initial load to prevent scroll jumping on refreshes
  const isInitialLoad = useRef(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Auto-enable monitoring after returning from Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('monitoring_subscribed') === 'true') {
      const savedSettings = localStorage.getItem('pendingMonitoringSettings');
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        // Only auto-enable for this project
        if (settings.projectId === projectId) {
          const token = localStorage.getItem('token');
          fetch(`/api/monitoring/projects/${projectId}/enable`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(settings)
          }).then(response => {
            if (response.ok) {
              localStorage.removeItem('pendingMonitoringSettings');
              // Reload page without query params to show updated state
              window.location.href = window.location.pathname;
            } else {
              console.error('Failed to auto-enable monitoring after subscription');
            }
          }).catch(err => {
            console.error('Error auto-enabling monitoring:', err);
          });
        }
      }
      // Clean up the URL param even if no saved settings
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [projectId]);

  // Load status on mount and every 30 seconds
  useEffect(() => {
    loadStatus(!isInitialLoad.current); // Pass silent flag on refreshes
    loadUsageStats();
    if (activeTab === 'bd') {
      loadBDInsights();
      loadBDAlerts(!isInitialLoad.current);
    }
    if (activeTab === 'candidate') {
      loadAlerts(!isInitialLoad.current);
    }
    isInitialLoad.current = false;

    const interval = setInterval(() => {
      // Silent refresh - don't show loading states to prevent scroll jumping
      loadStatus(true);
      loadUsageStats();
      if (activeTab === 'bd') {
        loadBDInsights();
        loadBDAlerts(true);
      }
      if (activeTab === 'candidate') {
        loadAlerts(true);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [activeTab]);

  // Reload candidate alerts when filters change
  useEffect(() => {
    if (activeTab === 'candidate') {
      loadAlerts(false);
    }
  }, [alertFilters]);

  // Reload BD alerts when filters change
  useEffect(() => {
    if (activeTab === 'bd') {
      loadBDAlerts(false);
    }
  }, [bdAlertFilters, includeOldMoves]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadStatus = async (silent: boolean = false) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/monitoring/status?projectId=${projectId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error('Error loading monitoring status:', error);
    }
  };

  const loadUsageStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/monitoring/usage-stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsageStats(data);
      }
    } catch (error) {
      console.error('Error loading usage stats:', error);
    }
  };

  const loadBDInsights = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/monitoring/projects/${projectId}/bd-insights`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBdInsights(data);
      }
    } catch (error) {
      console.error('Error loading BD insights:', error);
    }
  };

  const loadAlerts = async (silent: boolean = false) => {
    if (!silent) setAlertsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('projectId', projectId.toString());
      if (alertFilters.status !== 'all') params.append('status', alertFilters.status);
      if (alertFilters.priority !== 'all') params.append('priority', alertFilters.priority);
      if (alertFilters.alertType !== 'all') {
        params.append('alertType', alertFilters.alertType);
      } else {
        // Default view: restrict to candidate-side signals server-side so the
        // limit is spent on THESE types (not high-priority BD/open_to_work
        // alerts), otherwise medium-priority profile changes never surface.
        params.append('alertTypes', 'high_risk_candidate,critical_risk_candidate,open_to_work,profile_change,promotion');
      }
      params.append('limit', '300');

      const response = await fetch(`/api/monitoring/alerts?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Candidate-side signals = delivery signals about the CANDIDATE:
        // flight risk, open-to-work, profile changes, and promotions
        // (same-employer role change). Inter-company job changes are a BD
        // lead (you contact the company), so they live in BD Intelligence,
        // NOT here.
        const candidateAlerts = data.alerts?.filter((a: any) =>
          ['high_risk_candidate', 'critical_risk_candidate', 'open_to_work', 'profile_change', 'promotion'].includes(a.alert_type)
        ) || [];
        setAlerts(candidateAlerts);
      }
    } catch (error) {
      console.error('Error loading alerts:', error);
    } finally {
      if (!silent) setAlertsLoading(false);
    }
  };

  const loadBDAlerts = async (silent: boolean = false) => {
    if (!silent) setBdAlertsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('projectId', projectId.toString());
      if (bdAlertFilters.status !== 'all') params.append('status', bdAlertFilters.status);
      if (bdAlertFilters.priority !== 'all') params.append('priority', bdAlertFilters.priority);
      if (bdAlertFilters.alertType !== 'all') {
        params.append('alertType', bdAlertFilters.alertType);
      } else {
        // Restrict to BD-side signal types server-side so the limit is spent on
        // these (not candidate alerts).
        params.append('alertTypes', 'company_hiring_trend,company_movement,new_hire_detected,job_change');
      }
      params.append('limit', '300');
      if (includeOldMoves) params.append('includeOld', 'true');

      const response = await fetch(`/api/monitoring/alerts?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // BD-related alerts. job_change (inter-company move) is a BD lead —
        // the old company may need to backfill and the new company just hired.
        const bdOnlyAlerts = data.alerts?.filter((a: any) =>
          ['company_hiring_trend', 'company_movement', 'new_hire_detected', 'job_change'].includes(a.alert_type)
        ) || [];
        setBdAlerts(bdOnlyAlerts);
        setOlderMoves(Number(data.olderMoves) || 0);
      }
    } catch (error) {
      console.error('Error loading BD alerts:', error);
    } finally {
      if (!silent) setBdAlertsLoading(false);
    }
  };

  const markAlertComplete = async (alertId: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/monitoring/alerts/${alertId}/mark-complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Refresh both alert lists
        loadAlerts();
        loadBDAlerts();
      }
    } catch (error) {
      console.error('Error marking alert complete:', error);
    }
  };

  const snoozeAlert = async (alertId: number, days: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/monitoring/alerts/${alertId}/snooze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ days })
      });

      if (response.ok) {
        // Refresh both alert lists
        loadAlerts();
        loadBDAlerts();
      }
    } catch (error) {
      console.error('Error snoozing alert:', error);
    }
  };

  const dismissAlert = async (alertId: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/monitoring/alerts/${alertId}/dismiss`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Refresh both alert lists
        loadAlerts();
        loadBDAlerts();
      }
    } catch (error) {
      console.error('Error dismissing alert:', error);
    }
  };

  const initializeCandidates = async () => {
    setInitializing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/monitoring/projects/${projectId}/initialize-candidates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        alert(`✅ ${data.message}`);

        // Reload status after initialization
        setTimeout(loadStatus, 1000);
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error initializing candidates:', error);
      alert('Failed to initialize candidates');
    } finally {
      setInitializing(false);
    }
  };

  const runMonitoring = async (batchSize: number = 10) => {
    setRunning(true);
    setLastRunResult(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/monitoring/run', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ batchSize })
      });

      if (response.ok) {
        const data = await response.json();
        setLastRunResult(data);

        // Reload status and BD insights after run
        setTimeout(() => {
          loadStatus();
          loadBDInsights();
        }, 1000);
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error running monitoring:', error);
      alert('Failed to run monitoring');
    } finally {
      setRunning(false);
    }
  };

  const processAllRemaining = async () => {
    if (!status) {
      alert('Please wait for status to load');
      return;
    }

    if (!window.confirm(`This will process all ${status.candidatesDue} remaining candidates in batches of 50. This may take several minutes. Continue?`)) {
      return;
    }

    setRunning(true);
    let totalProcessed = 0;
    let totalWithChanges = 0;
    const batchSize = 50;

    try {
      while (true) {
        // Check how many are still due
        await loadStatus();
        if (!status || status.candidatesDue === 0) {
          break;
        }

        // Process one batch
        const token = localStorage.getItem('token');
        const response = await fetch('/api/monitoring/run', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ batchSize })
        });

        if (response.ok) {
          const data = await response.json();
          const successful = data.results.filter((r: any) => r.success).length;
          const withChanges = data.results.filter((r: any) => r.success && r.changes > 0).length;

          totalProcessed += successful;
          totalWithChanges += withChanges;

          console.log(`Processed batch: ${successful} candidates, ${withChanges} with changes`);

          // Small delay between batches
          await new Promise(resolve => setTimeout(resolve, 500));
        } else {
          console.error('Batch failed');
          break;
        }
      }

      alert(`✅ Processing complete!\n\nProcessed: ${totalProcessed} candidates\nWith changes: ${totalWithChanges}\n\nRefresh the page to see updated data.`);

      // Reload everything
      await loadStatus();
      await loadBDInsights();

    } catch (error) {
      console.error('Error processing all candidates:', error);
      alert('Error processing candidates. Check console for details.');
    } finally {
      setRunning(false);
    }
  };

  const toggleRowExpansion = (candidateId: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(candidateId)) {
      newExpanded.delete(candidateId);
    } else {
      newExpanded.add(candidateId);
    }
    setExpandedRows(newExpanded);
  };

  const getRiskColor = (tier: string) => {
    switch (tier) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'very_stable': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getAlertPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800 border-l-4 border-red-500';
      case 'high': return 'bg-orange-100 text-orange-800 border-l-4 border-orange-500';
      case 'medium': return 'bg-yellow-50 text-yellow-800 border-l-4 border-yellow-500';
      case 'low': return 'bg-blue-50 text-blue-800 border-l-4 border-blue-500';
      default: return 'bg-gray-50 text-gray-800 border-l-4 border-gray-500';
    }
  };

  // Get human-readable change tags for an alert
  const getAlertChangeTags = (alert: any): string[] => {
    const tags: string[] = [];

    // Check alert type
    if (alert.alert_type === 'open_to_work') {
      tags.push('Open to Work');
    }
    if (alert.alert_type === 'job_change' || alert.alert_type === 'company_movement') {
      tags.push('New Company');
    }
    if (alert.alert_type === 'critical_risk_candidate' || alert.alert_type === 'high_risk_candidate') {
      tags.push('Flight Risk');
    }

    // Check metadata for specific changes
    if (Array.isArray(alert.metadata?.changes)) {
      for (const change of alert.metadata.changes) {
        if (change.type === 'company' || change.change_type === 'company') {
          // Check if it's a promotion (same company, different title) or new company
          const oldCompany = change.oldValue?.company || '';
          const newCompany = change.newValue?.company || '';
          if (oldCompany && newCompany && oldCompany.toLowerCase() === newCompany.toLowerCase()) {
            if (!tags.includes('Promotion')) tags.push('Promotion');
          } else {
            if (!tags.includes('New Company')) tags.push('New Company');
          }
        }
        if (change.type === 'headline' || change.change_type === 'headline') {
          if (!tags.includes('Headline Update')) tags.push('Headline Update');
        }
        if (change.type === 'summary' || change.change_type === 'summary') {
          if (!tags.includes('Profile Changes')) tags.push('Profile Changes');
        }
        if (change.type === 'skills' || change.change_type === 'skills') {
          if (!tags.includes('Skills Update')) tags.push('Skills Update');
        }
        if (change.type === 'open_to_work' || change.change_type === 'open_to_work') {
          if (!tags.includes('Open to Work')) tags.push('Open to Work');
        }
        if (change.type === 'current_role_description' || change.change_type === 'current_role_description') {
          if (!tags.includes('Role Description')) tags.push('Role Description');
        }
      }
    }

    // Default if no specific tags
    if (tags.length === 0 && alert.alert_type === 'profile_change') {
      tags.push('Profile Changes');
    }

    return tags;
  };

  // Where a candidate moved from → to (for job-change signals). Prefers the
  // explicit fromCompany/toCompany in metadata, falling back to the company
  // change entry. Returns null for same-company moves (promotions) and when
  // there's no company change.
  const monthLabel = (ym?: string | null) => {
    if (!ym) return null;
    const [y, m] = ym.split('-').map(Number);
    if (!y) return null;
    return m ? new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' }) : String(y);
  };
  const sortBd = <T extends { latest_move?: string }>(list: T[], count: (c: T) => number): T[] =>
    bdSort === 'recent'
      ? [...list].sort((a, b) => String(b.latest_move || '').localeCompare(String(a.latest_move || '')))
      : [...list].sort((a, b) => count(b) - count(a));
  const bdSortToggle = (
    <div className="inline-flex rounded-md border border-gray-200 overflow-hidden text-xs shrink-0">
      {([['count', 'Most people'], ['recent', 'Most recent move']] as const).map(([k, label]) => (
        <button key={k} onClick={() => setBdSort(k)}
          className={`px-2.5 py-1 ${bdSort === k ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
          {label}
        </button>
      ))}
    </div>
  );

  const getMove = (alert: any): { from: string; to: string; moveDate: string | null } | null => {
    const m = alert.metadata || {};
    let from = m.fromCompany || '';
    let to = m.toCompany || '';
    if ((!from || !to) && Array.isArray(m.changes)) {
      const comp = m.changes.find((c: any) => c.type === 'company' || c.change_type === 'company');
      if (comp) {
        from = from || comp.oldValue?.company || '';
        to = to || comp.newValue?.company || '';
      }
    }
    if (!from || !to) return null;
    if (from.toLowerCase() === to.toLowerCase()) return null; // same company = promotion
    return { from, to, moveDate: m.moveDate || null };
  };

  // Expandable "company & contacts" panel for a signal — lets a user see the
  // company's mapped data + hiring managers and chase them up in place.
  const renderCompanyContext = (ctxKey: number, companyName: string | undefined | null, label?: string) => {
    if (!companyName) return null;
    const isOpen = companyCtxOpen.has(ctxKey);
    const ctx = companyCtx[ctxKey];
    return (
      <div className="mt-2">
        <button
          onClick={() => toggleCompanyContext(ctxKey, companyName)}
          className="text-xs text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-1"
        >
          🏢 {label ? `${label} · ` : ''}{companyName} — {isOpen ? 'hide details' : 'company & contacts'}
        </button>
        {isOpen && (
          <div className="mt-2 p-3 bg-white/70 border border-gray-200 rounded-lg text-xs">
            {ctx?.loading ? (
              <span className="text-gray-500">Loading company & contacts…</span>
            ) : (
              <>
                {ctx?.company ? (
                  <div className="mb-2">
                    <div className="font-semibold text-gray-800">{ctx.company.company_name}</div>
                    <div className="text-gray-600 flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                      {ctx.company.industry && <span>🏭 {ctx.company.industry}</span>}
                      {ctx.company.employee_count && <span>👥 {ctx.company.employee_count}</span>}
                      {(ctx.company.headquarters || ctx.company.city) && <span>📍 {ctx.company.headquarters || ctx.company.city}</span>}
                      {ctx.company.website && <a href={ctx.company.website.startsWith('http') ? ctx.company.website : `https://${ctx.company.website}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">🌐 Website</a>}
                      {ctx.company.url && <a href={ctx.company.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">in LinkedIn</a>}
                    </div>
                    {ctx.company.specialties && (
                      <p className="text-gray-500 mt-1">
                        <span className="font-medium text-gray-600">🏷️ Specialties:</span>{' '}
                        {Array.isArray(ctx.company.specialties) ? ctx.company.specialties.join(', ') : ctx.company.specialties}
                      </p>
                    )}
                    {ctx.company.description && <p className="text-gray-500 mt-1 line-clamp-2">{ctx.company.description}</p>}
                  </div>
                ) : (
                  <div className="text-gray-500 mb-2">This company isn't mapped in this project's data table.</div>
                )}
                <div className="font-semibold text-gray-700 mb-1">Contacts ({ctx?.contacts?.length || 0})</div>
                {ctx?.contacts && ctx.contacts.length > 0 ? (
                  <ul className="space-y-1">
                    {ctx.contacts.map((c: any) => (
                      <li key={c.id} className="flex items-center justify-between gap-2 border-b border-gray-100 pb-1 last:border-0">
                        <span className="truncate">
                          <span className="font-medium text-gray-800">{c.name}</span>
                          {c.job_title && <span className="text-gray-500"> · {c.job_title}</span>}
                        </span>
                        <span className="flex items-center gap-2 whitespace-nowrap">
                          {c.email && <a href={`mailto:${c.email}`} className="text-blue-600 hover:underline" title={c.email}>✉️</a>}
                          {c.phone && <a href={`tel:${c.phone}`} className="text-blue-600 hover:underline" title={c.phone}>📞</a>}
                          {c.li_profile_url && <a href={c.li_profile_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline" title="LinkedIn">in</a>}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-gray-500">No mapped contacts for this company yet.</div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  // Inline company facts (industry / headcount / HQ / specialties / LinkedIn)
  // for the BD Intelligence detection cards. Links stopPropagation so clicking
  // one doesn't toggle the card's expand/collapse.
  const renderBdCompanyMeta = (company: any) => {
    if (!company) return null;
    const hasAny = company.industry || company.employee_count || company.headquarters ||
      company.specialties || company.company_linkedin_url || company.website;
    if (!hasAny) {
      return (
        <p className="text-[11px] text-gray-400 mt-1">Company not mapped/enriched in this project's data.</p>
      );
    }
    const stop = (e: any) => e.stopPropagation();
    const websiteHref = company.website
      ? (company.website.startsWith('http') ? company.website : `https://${company.website}`)
      : null;
    return (
      <div className="mt-1.5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-600">
          {company.industry && <span>🏭 {company.industry}</span>}
          {company.employee_count && <span>👥 {company.employee_count}</span>}
          {company.headquarters && <span>📍 {company.headquarters}</span>}
          {websiteHref && <a href={websiteHref} onClick={stop} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">🌐 Website</a>}
          {company.company_linkedin_url && <a href={company.company_linkedin_url} onClick={stop} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">in LinkedIn</a>}
        </div>
        {company.specialties && (
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
            <span className="font-medium text-gray-600">🏷️ Specialties:</span>{' '}
            {Array.isArray(company.specialties) ? company.specialties.join(', ') : company.specialties}
          </p>
        )}
      </div>
    );
  };

  const formatChangeDescription = (change: any) => {
    try {
      const oldVal = change.old_value ? JSON.parse(change.old_value) : null;
      const newVal = change.new_value ? JSON.parse(change.new_value) : null;

      switch (change.change_type) {
        case 'headline':
          return `Headline: "${oldVal}" → "${newVal}"`;
        case 'summary':
          return 'Profile summary updated';
        case 'skills':
          return `Skills updated`;
        case 'experience':
          return 'Role description updated';
        case 'job_change':
          return `Job change: ${oldVal?.title} at ${oldVal?.company} → ${newVal?.title} at ${newVal?.company}`;
        case 'open_to_work':
          return '🚨 Marked as "Open to work"';
        default:
          return 'Profile updated';
      }
    } catch {
      return change.field_name || 'Profile updated';
    }
  };

  const toggleCompanyExpansion = (companyName: string) => {
    const newExpanded = new Set(expandedCompanies);
    if (newExpanded.has(companyName)) {
      newExpanded.delete(companyName);
    } else {
      newExpanded.add(companyName);
      loadBdContacts(companyName); // lazy-load mapped contacts on expand
    }
    setExpandedCompanies(newExpanded);
  };

  const markCompanyComplete = (companyName: string) => {
    const newCompleted = new Set(completedCompanies);
    newCompleted.add(companyName);
    setCompletedCompanies(newCompleted);
  };

  const snoozeCompany = (companyName: string, days: number) => {
    // For now, just mark as completed (can enhance later with localStorage)
    markCompanyComplete(companyName);
  };

  const dismissCompany = (companyName: string) => {
    markCompanyComplete(companyName);
  };

  // Open alert detail modal
  const openAlertDetail = (alert: any) => {
    setSelectedAlert(alert);
    setShowAlertDetailModal(true);
  };

  // Get detailed change information for modal display
  const getDetailedChanges = (alert: any) => {
    const details: Array<{
      category: string;
      changes: Array<{ label: string; oldValue: string; newValue: string }>;
    }> = [];

    if (!alert.metadata?.changes) return details;

    // Group changes by category
    const profileChanges: Array<{ label: string; oldValue: string; newValue: string }> = [];
    const jobChanges: Array<{ label: string; oldValue: string; newValue: string }> = [];
    const otherChanges: Array<{ label: string; oldValue: string; newValue: string }> = [];

    for (const change of (Array.isArray(alert.metadata?.changes) ? alert.metadata.changes : [])) {
      try {
        const oldVal = change.old_value ? JSON.parse(change.old_value) : change.oldValue;
        const newVal = change.new_value ? JSON.parse(change.new_value) : change.newValue;
        const changeType = change.change_type || change.type;

        if (changeType === 'company' || changeType === 'current_position') {
          const oldCompany = oldVal?.company || '';
          const newCompany = newVal?.company || '';
          const oldTitle = oldVal?.title || '';
          const newTitle = newVal?.title || '';

          if (oldCompany !== newCompany) {
            jobChanges.push({
              label: 'Company',
              oldValue: oldCompany || '(none)',
              newValue: newCompany || '(none)'
            });
          }
          if (oldTitle !== newTitle) {
            jobChanges.push({
              label: 'Job Title',
              oldValue: oldTitle || '(none)',
              newValue: newTitle || '(none)'
            });
          }
        } else if (changeType === 'headline') {
          profileChanges.push({
            label: 'Headline',
            oldValue: typeof oldVal === 'string' ? oldVal : JSON.stringify(oldVal) || '(none)',
            newValue: typeof newVal === 'string' ? newVal : JSON.stringify(newVal) || '(none)'
          });
        } else if (changeType === 'summary') {
          profileChanges.push({
            label: 'Summary',
            oldValue: oldVal ? (oldVal.length > 100 ? oldVal.substring(0, 100) + '...' : oldVal) : '(none)',
            newValue: newVal ? (newVal.length > 100 ? newVal.substring(0, 100) + '...' : newVal) : '(none)'
          });
        } else if (changeType === 'skills') {
          otherChanges.push({
            label: 'Skills',
            oldValue: 'Previous skills',
            newValue: 'Skills updated'
          });
        } else if (changeType === 'open_to_work') {
          otherChanges.push({
            label: 'Open to Work',
            oldValue: oldVal ? 'Yes' : 'No',
            newValue: newVal ? 'Yes' : 'No'
          });
        } else if (changeType === 'current_role_description') {
          profileChanges.push({
            label: 'Role Description',
            oldValue: oldVal ? (oldVal.length > 100 ? oldVal.substring(0, 100) + '...' : oldVal) : '(none)',
            newValue: newVal ? (newVal.length > 100 ? newVal.substring(0, 100) + '...' : newVal) : '(none)'
          });
        } else {
          otherChanges.push({
            label: change.description || changeType,
            oldValue: typeof oldVal === 'string' ? oldVal : JSON.stringify(oldVal) || '(none)',
            newValue: typeof newVal === 'string' ? newVal : JSON.stringify(newVal) || '(none)'
          });
        }
      } catch (e) {
        // Skip malformed changes
      }
    }

    if (jobChanges.length > 0) {
      details.push({ category: 'Job Changes', changes: jobChanges });
    }
    if (profileChanges.length > 0) {
      details.push({ category: 'Profile Changes', changes: profileChanges });
    }
    if (otherChanges.length > 0) {
      details.push({ category: 'Other Changes', changes: otherChanges });
    }

    return details;
  };

  if (!status) {
    return (
      <div className="card p-6">
        <p className="text-gray-600 animate-fade-in">Loading monitoring status...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl shadow-elevated p-6 animate-fade-in max-md:p-4">
        <div className="flex justify-between items-start max-md:flex-col max-md:gap-3">
          <div>
            <h2 className="text-2xl font-bold mb-2 tracking-tight max-md:text-xl">📡 Monitoring Dashboard</h2>
            <p className="text-purple-100">
              Project: {projectName}
            </p>
            <div className="mt-3 inline-block px-3 py-1 bg-white/20 rounded-full text-sm">
              {status.projectMonitoringEnabled ? (
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                  Active - {status.totalMonitored} candidates monitored
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                  Not Active
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 max-md:flex-wrap">
            <button
              onClick={() => setShowIntelReport(true)}
              className="px-4 py-2 bg-white text-purple-700 hover:bg-purple-50 rounded-lg font-semibold transition-all duration-200 flex items-center gap-2 hover:-translate-y-px hover:shadow-md"
              title="Board-ready Talent Market Intelligence report"
            >
              ✨ Intelligence Report
            </button>
            <button
              onClick={() => setShowReportModal(true)}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 hover:-translate-y-px hover:shadow-md"
              title="Download a historical statement of monitoring signals (PDF/CSV)"
            >
              📄 Statement
            </button>
            <button
              onClick={() => setShowSettingsModal(true)}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 hover:-translate-y-px hover:shadow-md"
            >
              ⚙️ Configure Monitoring
            </button>
          </div>
        </div>

        {/* Cross-project monitoring notice */}
        {!status.projectMonitoringEnabled && status.crossProjectMonitored > 0 && (
          <div className="mt-4 pt-4 border-t border-white/20">
            <div className="bg-white/10 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <span className="text-blue-300 mt-0.5">&#x1f517;</span>
                <div>
                  <p className="font-medium">
                    {status.crossProjectMonitored} of {status.totalCandidatesInProject} candidates receiving updates via other projects
                  </p>
                  <p className="text-sm text-purple-200 mt-1">
                    {status.crossProjectSources.map(s =>
                      `${s.name} (${s.candidate_count} candidates)`
                    ).join(', ')}
                  </p>
                  {status.crossProjectMonitored < status.totalCandidatesInProject && (
                    <p className="text-sm text-yellow-300 mt-1">
                      {status.totalCandidatesInProject - status.crossProjectMonitored} candidates in this project are not being monitored.
                      Enable monitoring to cover them.
                    </p>
                  )}
                  {status.crossProjectMonitored >= status.totalCandidatesInProject && (
                    <p className="text-sm text-green-300 mt-1">
                      All candidates in this project are already covered. No need to enable monitoring separately.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Usage Stats Bar - Only show if monitoring is active on THIS project */}
        {status.projectMonitoringEnabled && usageStats?.hasSubscription && usageStats.subscription && (
          <div className="mt-4 pt-4 border-t border-white/20">
            <p className="text-xs text-purple-200 mb-2">Account-wide usage (all projects)</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Plan Info */}
              <div>
                <p className="text-xs text-purple-200 mb-1">Plan</p>
                <p className="font-semibold">{usageStats.subscription.planName}</p>
              </div>

              {/* Monthly Usage */}
              <div>
                <p className="text-xs text-purple-200 mb-1">Monthly Usage</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-white/20 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        usageStats.subscription.usagePercentage > 90 ? 'bg-red-400' :
                        usageStats.subscription.usagePercentage > 75 ? 'bg-yellow-400' :
                        'bg-green-400'
                      }`}
                      style={{ width: `${Math.min(usageStats.subscription.usagePercentage, 100)}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium">{usageStats.subscription.usagePercentage}%</span>
                </div>
                <p className="text-xs text-purple-200 mt-1">
                  {usageStats.subscription.creditsUsedThisCycle.toLocaleString()} / {usageStats.subscription.includedChecks.toLocaleString()} checks
                </p>
              </div>

              {/* Today's Stats */}
              {usageStats.today && (
                <div>
                  <p className="text-xs text-purple-200 mb-1">Today</p>
                  <p className="font-semibold">{usageStats.today.checksPerformed} checks</p>
                  {usageStats.today.changesDetected > 0 && (
                    <p className="text-xs text-yellow-300">{usageStats.today.changesDetected} changes detected</p>
                  )}
                </div>
              )}

              {/* This Week */}
              {usageStats.thisWeek && (
                <div>
                  <p className="text-xs text-purple-200 mb-1">This Week</p>
                  <p className="font-semibold">{usageStats.thisWeek.checksPerformed} checks</p>
                  {usageStats.thisWeek.changesDetected > 0 && (
                    <p className="text-xs text-yellow-300">{usageStats.thisWeek.changesDetected} changes</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* No subscription notice - only show when monitoring is not active on this project */}
        {!status.projectMonitoringEnabled && status.crossProjectMonitored === 0 && usageStats && !usageStats.hasSubscription && (
          <div className="mt-4 pt-4 border-t border-white/20">
            <div className="bg-white/10 rounded-lg p-3 flex items-center justify-between">
              <div>
                <p className="font-medium">No Monitoring Subscription</p>
                <p className="text-sm text-purple-200">Subscribe to enable automated candidate monitoring</p>
              </div>
              <button
                onClick={() => setShowSettingsModal(true)}
                className="px-4 py-2 bg-white text-purple-600 rounded-lg font-medium hover:bg-purple-50 transition-all duration-200 hover:-translate-y-px hover:shadow-md"
              >
                View Plans
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tab bar */}
      <div className="card p-2 flex gap-1.5 flex-wrap">
        {([
          { key: 'overview', icon: '📊', label: 'Overview', sub: 'The period at a glance' },
          { key: 'candidate', icon: '🎯', label: 'Candidate Signals', sub: 'Flight risk & profile changes' },
          { key: 'movements', icon: '🗺️', label: 'Movements', sub: 'Who moved where' },
          { key: 'bd', icon: '💼', label: 'BD Intelligence', sub: 'Hiring trends & attrition' },
          { key: 'report', icon: '📄', label: 'Report', sub: 'Summary & export' },
        ] as const).map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`flex-1 min-w-[9rem] px-4 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 ${
              activeTab === t.key
                ? 'bg-purple-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm'
            }`}
          >
            {t.icon} {t.label}
            <span className="hidden lg:block text-[11px] font-normal mt-0.5 opacity-80">{t.sub}</span>
          </button>
        ))}
      </div>

      {/* Period chips — shared by the Overview / Movements / Report tabs */}
      {(activeTab === 'overview' || activeTab === 'movements' || activeTab === 'report') && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-500 font-medium mr-1">Period</span>
          {periods.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriodKey(p.key)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                periodKey === p.key
                  ? 'bg-purple-600 text-white border-purple-600 shadow-sm'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-purple-300 hover:text-purple-700'
              }`}
            >
              {p.label}
            </button>
          ))}
          {reportLoading && <span className="text-xs text-gray-400 animate-pulse">updating…</span>}
        </div>
      )}

      {/* Overview */}
      {activeTab === 'overview' && (
        <>
          {/* System status — what monitoring is doing right now. Lives on
              Overview because these numbers span every signal type. */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="stat-card border-l-4 border-orange-500 transition-all duration-200 hover:shadow-card-hover">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Candidates Due</p>
                  <p className="text-3xl font-bold tracking-tight text-gray-900">{status.candidatesDue}</p>
                </div>
                <div className="text-4xl">⏰</div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Ready for monitoring check</p>
            </div>
            <div className="stat-card border-l-4 border-blue-500 transition-all duration-200 hover:shadow-card-hover">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Monitored</p>
                  <p className="text-3xl font-bold tracking-tight text-gray-900">{status.totalMonitored}</p>
                </div>
                <div className="text-4xl">👥</div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Active monitoring enabled</p>
            </div>
            <div className="stat-card border-l-4 border-green-500 transition-all duration-200 hover:shadow-card-hover">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">24h Activity</p>
                  <p className="text-3xl font-bold tracking-tight text-gray-900">
                    {status.recentExecutions.reduce((sum, e) => sum + parseInt(e.count), 0)}
                  </p>
                </div>
                <div className="text-4xl">📊</div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Checks performed today</p>
            </div>
          </div>

          <MonitoringOverview
            data={reportData}
            loading={reportLoading}
            onGoToMovements={() => setActiveTab('movements')}
            onGoToSignals={() => setActiveTab('candidate')}
          />
        </>
      )}

      {/* Movements */}
      {activeTab === 'movements' && (
        <React.Suspense fallback={<div className="card p-6"><p className="text-gray-600 animate-fade-in">Loading map…</p></div>}>
          <MonitoringMovements
            data={reportData}
            loading={reportLoading}
            onOpenCandidate={(candidateId) => setContactPanel({ open: true, entityId: candidateId, entityType: 'candidate' })}
          />
        </React.Suspense>
      )}

      {/* Report */}
      {activeTab === 'report' && (
        <MonitoringReportTab
          projectId={projectId}
          data={reportData}
          loading={reportLoading}
          from={activePeriod.from}
          to={activePeriod.to}
          onOpenStatement={() => setShowReportModal(true)}
        />
      )}

      {/* Candidate Signals View */}
      {activeTab === 'candidate' && (
        <>
          {/* Persistent Alerts Section */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold tracking-tight">🔔 Monitoring Alerts</h3>
              <button
                onClick={() => loadAlerts(false)}
                className="btn-ghost text-xs"
              >
                🔄 Refresh
              </button>
            </div>

            {/* Alert Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <div>
                <label className="section-label mb-1 block">Status</label>
                <select
                  value={alertFilters.status}
                  onChange={(e) => setAlertFilters({...alertFilters, status: e.target.value})}
                  className="input"
                >
                  <option value="unread">Unread Only</option>
                  <option value="all">All Alerts</option>
                  <option value="read">Read</option>
                  <option value="completed">Completed</option>
                  <option value="snoozed">Snoozed</option>
                </select>
              </div>
              <div>
                <label className="section-label mb-1 block">Priority</label>
                <select
                  value={alertFilters.priority}
                  onChange={(e) => setAlertFilters({...alertFilters, priority: e.target.value})}
                  className="input"
                >
                  <option value="all">All Priorities</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
              <div>
                <label className="section-label mb-1 block">Type</label>
                <select
                  value={alertFilters.alertType}
                  onChange={(e) => setAlertFilters({...alertFilters, alertType: e.target.value})}
                  className="input"
                >
                  <option value="all">All Types</option>
                  <option value="critical_risk_candidate">Critical Risk</option>
                  <option value="high_risk_candidate">High Risk</option>
                  <option value="open_to_work">Open to Work</option>
                  <option value="job_change">Job Changes</option>
                  <option value="promotion">Promotions</option>
                  <option value="profile_change">Profile Changes</option>
                </select>
              </div>
            </div>

            {/* Persistent per-type filter — click to hide/show a signal type.
                Your choice is saved so it doesn't reset next time. */}
            <div className="mb-3 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500">Show:</span>
              {[
                { type: 'promotion', label: 'Promotions' },
                { type: 'open_to_work', label: 'Open to Work' },
                { type: 'profile_change', label: 'Profile Changes' },
                { type: 'high_risk_candidate', label: 'Flight Risk' },
                { type: 'critical_risk_candidate', label: 'Critical Risk' },
              ].map(s => {
                const hidden = hiddenSignalTypes.has(s.type);
                return (
                  <button
                    key={s.type}
                    onClick={() => toggleHiddenSignalType(s.type)}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                      hidden ? 'bg-gray-100 text-gray-400 border-gray-200 line-through' : 'bg-purple-50 text-purple-700 border-purple-200'
                    }`}
                    title={hidden ? 'Hidden — click to show' : 'Showing — click to hide'}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>

            {/* Alerts List */}
            {alertsLoading ? (
              <div className="text-center py-8">
                <p className="text-gray-500">Loading alerts...</p>
              </div>
            ) : alerts.filter((a: any) => (!hiddenSignalTypes.has(a.alert_type) || a.id === focusAlertId)).length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-1">✅ No alerts match your filters</p>
                <p className="text-xs text-gray-400">
                  {alerts.length > 0 ? 'All matching signals are hidden by your type filter above.' : 'Run monitoring to generate new alerts'}
                </p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {alerts.filter((a: any) => (!hiddenSignalTypes.has(a.alert_type) || a.id === focusAlertId)).map((alert: any) => {
                  const changeTags = getAlertChangeTags(alert);
                  const isRecent = alert.created_at && (Date.now() - new Date(alert.created_at).getTime()) < 48 * 3600 * 1000;
                  return (
                  <div
                    key={alert.id}
                    id={`mon-alert-${alert.id}`}
                    className={`rounded-lg p-4 ${getAlertPriorityColor(alert.priority)} ${
                      alert.id === focusAlertId ? 'ring-2 ring-purple-500 ring-offset-2 animate-pulse' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* Alert Content */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-gray-900">{alert.title}</h4>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            alert.priority === 'critical' ? 'bg-red-200 text-red-900' :
                            alert.priority === 'high' ? 'bg-orange-200 text-orange-900' :
                            alert.priority === 'medium' ? 'bg-yellow-200 text-yellow-900' :
                            'bg-blue-200 text-blue-900'
                          }`}>
                            {alert.priority.toUpperCase()}
                          </span>
                        </div>

                        {/* Change tags in smaller text */}
                        {changeTags.length > 0 && (
                          <p className="text-xs text-gray-500 mb-2">
                            {changeTags.join(', ')}
                          </p>
                        )}

                        <p className="text-sm text-gray-700 mb-2 whitespace-pre-line">{alert.message}</p>

                        {/* Display detailed changes if available */}
                        {Array.isArray(alert.metadata?.changes) && alert.metadata.changes.length > 0 && (
                          <div className="mb-2 mt-2 pl-4 border-l-2 border-gray-300">
                            <p className="text-xs font-semibold text-gray-600 mb-1">Changes detected:</p>
                            <ul className="text-xs text-gray-700 space-y-1">
                              {alert.metadata.changes.map((change: any, idx: number) => (
                                <li key={idx} className="flex items-start gap-2">
                                  <span className="text-gray-400">•</span>
                                  <span>
                                    <span className="font-medium">{change.description}</span>
                                    {change.example && <span className="text-gray-500 ml-1">({change.example})</span>}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <div className="flex items-center gap-3 text-xs text-gray-600 flex-wrap mt-2">
                          <span className={isRecent ? 'text-green-700 font-semibold' : ''}>
                            🔔 Detected {new Date(alert.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            {isRecent && <span className="ml-1 px-1.5 py-0.5 rounded bg-green-100 text-green-800 text-[10px] font-bold">NEW</span>}
                          </span>
                          {alert.metadata?.moveDate && <span>• 📅 Moved {alert.metadata.moveDate}</span>}
                          {alert.candidate_name && (
                            <span>• 👤{' '}
                              {alert.candidate_id ? (
                                <button
                                  className="text-blue-600 hover:underline font-medium"
                                  title="Open candidate panel"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setContactPanel({ open: true, entityId: alert.candidate_id, entityType: 'candidate' });
                                  }}
                                >
                                  {alert.candidate_name}
                                </button>
                              ) : (
                                alert.candidate_name
                              )}
                              {alert.candidate_job_title && <span className="text-gray-500"> · {alert.candidate_job_title}</span>}
                            </span>
                          )}
                          {alert.candidate_linkedin_url && (
                            <a
                              href={alert.candidate_linkedin_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-600 hover:underline font-medium"
                              title="Candidate LinkedIn profile"
                            >
                              • in LinkedIn
                            </a>
                          )}
                          {alert.project_name && <span>• 📁 {alert.project_name}</span>}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => openAlertDetail(alert)}
                          className="btn-primary text-xs py-1 px-3 whitespace-nowrap"
                        >
                          View Details
                        </button>
                        <button
                          onClick={() => markAlertComplete(alert.id)}
                          className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs font-medium whitespace-nowrap transition-all duration-200 hover:-translate-y-px"
                        >
                          ✓ Complete
                        </button>
                        <div className="relative group">
                          <button className="w-full px-3 py-1 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-xs font-medium whitespace-nowrap transition-all duration-200">
                            ⏰ Snooze
                          </button>
                          <div className="hidden group-hover:block absolute right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-elevated z-10 whitespace-nowrap overflow-hidden">
                            <button
                              onClick={() => snoozeAlert(alert.id, 1)}
                              className="block w-full px-4 py-2 text-left text-xs hover:bg-gray-50 transition-colors"
                            >
                              1 day
                            </button>
                            <button
                              onClick={() => snoozeAlert(alert.id, 3)}
                              className="block w-full px-4 py-2 text-left text-xs hover:bg-gray-50 transition-colors"
                            >
                              3 days
                            </button>
                            <button
                              onClick={() => snoozeAlert(alert.id, 7)}
                              className="block w-full px-4 py-2 text-left text-xs hover:bg-gray-50 transition-colors"
                            >
                              7 days
                            </button>
                            <button
                              onClick={() => snoozeAlert(alert.id, 30)}
                              className="block w-full px-4 py-2 text-left text-xs hover:bg-gray-50 transition-colors"
                            >
                              30 days
                            </button>
                          </div>
                        </div>
                        <button
                          onClick={() => dismissAlert(alert.id)}
                          className="px-3 py-1 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-xs font-medium whitespace-nowrap transition-all duration-200"
                        >
                          ✕ Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>

        </>
      )}

      {/* BD Intelligence View */}
      {activeTab === 'bd' && (
        <>
          {!bdInsights ? (
            <div className="card p-6">
              <p className="text-gray-600 animate-fade-in">Loading BD insights...</p>
            </div>
          ) : (
            <>
              {/* Market events — judged cohort corridors (acquisitions,
                  team lift-outs, rebrands) */}
              {cohortAlerts.length > 0 && (
                <div className="card p-6 border-l-4 border-violet-600">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold tracking-tight">📡 Market Events</h3>
                    <span className="text-xs text-gray-500">{cohortAlerts.length} detected in the last 90 days</span>
                  </div>
                  <div className="space-y-3">
                    {cohortAlerts.map((a: any) => (
                      <div key={a.id} className="rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900">{a.title}</p>
                            <p className="text-sm text-gray-600 mt-1">{a.message}</p>
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              {a.ai_category && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 font-medium">
                                  🧠 {AI_CATEGORY_LABELS[a.ai_category] || a.ai_category}
                                  {typeof a.ai_confidence === 'number' ? ` · ${Math.round(a.ai_confidence * 100)}%` : ''}
                                </span>
                              )}
                              <span className="text-xs text-gray-400">
                                {new Date(a.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={async () => { await dismissAlert(a.id); loadCohortAlerts(); }}
                            className="px-3 py-1 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-xs font-medium whitespace-nowrap"
                          >
                            ✕ Dismiss
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* BD Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="stat-card border-l-4 border-green-500 transition-all duration-200 hover:shadow-card-hover">
                  <p className="text-xs text-gray-600 mb-1">Total Hiring</p>
                  <p className="text-2xl font-bold tracking-tight text-green-600">{bdInsights.summary.totalHiring}</p>
                  <p className="text-xs text-gray-500 mt-1">Candidates joined this month</p>
                </div>

                <div className="stat-card border-l-4 border-red-500 transition-all duration-200 hover:shadow-card-hover">
                  <p className="text-xs text-gray-600 mb-1">Total Attrition</p>
                  <p className="text-2xl font-bold tracking-tight text-red-600">{bdInsights.summary.totalAttrition}</p>
                  <p className="text-xs text-gray-500 mt-1">Candidates left this month</p>
                </div>

                <div className="stat-card border-l-4 border-blue-500 transition-all duration-200 hover:shadow-card-hover">
                  <p className="text-xs text-gray-600 mb-1">Expanding Companies</p>
                  <p className="text-2xl font-bold tracking-tight text-blue-600">{bdInsights.summary.expandingCompanies}</p>
                  <p className="text-xs text-gray-500 mt-1">Net hiring +5 or more</p>
                </div>

                <div className="stat-card border-l-4 border-purple-500 transition-all duration-200 hover:shadow-card-hover">
                  <p className="text-xs text-gray-600 mb-1">New Companies</p>
                  <p className="text-2xl font-bold tracking-tight text-purple-600">{bdInsights.summary.newCompaniesDetected}</p>
                  <p className="text-xs text-gray-500 mt-1">Detected this month</p>
                </div>
              </div>

              {/* BD Intelligence Alerts section removed as noise — the company
                  data and mapped contacts it surfaced now live directly on the
                  Job Change / Backfill detection cards below. */}
              {false && (
              <div className="card p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold tracking-tight">🔔 BD Intelligence Alerts</h3>
                  <button
                    onClick={() => loadBDAlerts(false)}
                    className="btn-ghost text-xs"
                  >
                    🔄 Refresh
                  </button>
                </div>

                {/* BD Alert Filters */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                  <div>
                    <label className="section-label mb-1 block">Status</label>
                    <select
                      value={bdAlertFilters.status}
                      onChange={(e) => setBdAlertFilters({...bdAlertFilters, status: e.target.value})}
                      className="input"
                    >
                      <option value="unread">Unread Only</option>
                      <option value="all">All Alerts</option>
                      <option value="read">Read</option>
                      <option value="completed">Completed</option>
                      <option value="snoozed">Snoozed</option>
                    </select>
                  </div>
                  <div>
                    <label className="section-label mb-1 block">Priority</label>
                    <select
                      value={bdAlertFilters.priority}
                      onChange={(e) => setBdAlertFilters({...bdAlertFilters, priority: e.target.value})}
                      className="input"
                    >
                      <option value="all">All Priorities</option>
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                  <div>
                    <label className="section-label mb-1 block">Type</label>
                    <select
                      value={bdAlertFilters.alertType}
                      onChange={(e) => setBdAlertFilters({...bdAlertFilters, alertType: e.target.value})}
                      className="input"
                    >
                      <option value="all">All Types</option>
                      <option value="company_hiring_trend">Hiring Trends</option>
                      <option value="company_movement">Company Movements</option>
                      <option value="new_hire_detected">New Hires</option>
                    </select>
                  </div>
                </div>

                {olderMoves > 0 && (
                  <div className="mb-3 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 flex items-center justify-between gap-3 flex-wrap">
                    <span>
                      {includeOldMoves ? 'Showing' : `${olderMoves.toLocaleString()} older`} job {olderMoves === 1 && !includeOldMoves ? 'change' : 'changes'} {includeOldMoves ? `including ${olderMoves.toLocaleString()} older ones` : 'hidden'} — people who started their new role (or left the old one) more than 4 months before we spotted it, so it isn't news. Their records are still up to date.
                    </span>
                    <button className="text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap" onClick={() => setIncludeOldMoves((v) => !v)}>
                      {includeOldMoves ? 'Hide older moves' : 'Show older moves'}
                    </button>
                  </div>
                )}

                {/* BD Alerts List */}
                {bdAlertsLoading ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">Loading BD alerts...</p>
                  </div>
                ) : bdAlerts.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-1">✅ No BD alerts match your filters</p>
                    <p className="text-xs text-gray-400">Run monitoring to generate BD intelligence alerts</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {bdAlerts.map((alert: any) => {
                      const changeTags = getAlertChangeTags(alert);
                      const move = getMove(alert);
                      return (
                      <div key={alert.id} className={`rounded-lg p-4 ${getAlertPriorityColor(alert.priority)}`}>
                        <div className="flex items-start justify-between gap-4">
                          {/* Alert Content */}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-gray-900">{alert.title}</h4>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                alert.priority === 'critical' ? 'bg-red-200 text-red-900' :
                                alert.priority === 'high' ? 'bg-orange-200 text-orange-900' :
                                alert.priority === 'medium' ? 'bg-yellow-200 text-yellow-900' :
                                'bg-blue-200 text-blue-900'
                              }`}>
                                {alert.priority.toUpperCase()}
                              </span>
                            </div>

                            {/* Change tags in smaller text */}
                            {changeTags.length > 0 && (
                              <p className="text-xs text-gray-500 mb-2">
                                {changeTags.join(', ')}
                              </p>
                            )}

                            {/* Job change: where they moved from → to */}
                            {move && (
                              <div className="text-sm mb-2 flex items-center gap-1.5 flex-wrap bg-white/60 rounded px-2 py-1 border border-gray-200 w-fit">
                                <span className="text-gray-500">Left</span>
                                <span className="font-semibold text-gray-800">{move.from}</span>
                                <span className="text-gray-400">→</span>
                                <span className="text-gray-500">Now at</span>
                                <span className="font-semibold text-blue-700">{move.to}</span>
                              </div>
                            )}

                            <p className="text-sm text-gray-700 mb-2 whitespace-pre-line">{alert.message}</p>

                            {/* Company + mapped contacts. For a job change, offer BOTH the
                                old company (backfill lead) and the new one (just hired). */}
                            {alert.alert_type === 'job_change' && move ? (
                              <>
                                {renderCompanyContext(alert.id * 10 + 1, move.from, 'Backfill')}
                                {renderCompanyContext(alert.id * 10 + 2, move.to, 'New hire')}
                              </>
                            ) : (
                              renderCompanyContext(alert.id, alert.metadata?.companyName || alert.metadata?.company_name || alert.metadata?.company)
                            )}

                            <div className="flex items-center gap-3 text-xs text-gray-600 mt-2 flex-wrap">
                              <span>🔔 Detected {new Date(alert.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                              {alert.metadata?.moveDate && <span>• 📅 Moved {alert.metadata.moveDate}</span>}
                              {alert.project_name && <span>• 📁 {alert.project_name}</span>}
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => openAlertDetail(alert)}
                              className="btn-primary text-xs py-1 px-3 whitespace-nowrap"
                            >
                              View Details
                            </button>
                            <button
                              onClick={() => markAlertComplete(alert.id)}
                              className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs font-medium whitespace-nowrap transition-all duration-200 hover:-translate-y-px"
                            >
                              ✓ Complete
                            </button>
                            <div className="relative group">
                              <button className="w-full px-3 py-1 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-xs font-medium whitespace-nowrap transition-all duration-200">
                                ⏰ Snooze
                              </button>
                              <div className="hidden group-hover:block absolute right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-elevated z-10 whitespace-nowrap overflow-hidden">
                                <button
                                  onClick={() => snoozeAlert(alert.id, 1)}
                                  className="block w-full px-4 py-2 text-left text-xs hover:bg-gray-50 transition-colors"
                                >
                                  1 day
                                </button>
                                <button
                                  onClick={() => snoozeAlert(alert.id, 3)}
                                  className="block w-full px-4 py-2 text-left text-xs hover:bg-gray-50 transition-colors"
                                >
                                  3 days
                                </button>
                                <button
                                  onClick={() => snoozeAlert(alert.id, 7)}
                                  className="block w-full px-4 py-2 text-left text-xs hover:bg-gray-50 transition-colors"
                                >
                                  7 days
                                </button>
                                <button
                                  onClick={() => snoozeAlert(alert.id, 30)}
                                  className="block w-full px-4 py-2 text-left text-xs hover:bg-gray-50 transition-colors"
                                >
                                  30 days
                                </button>
                              </div>
                            </div>
                            <button
                              onClick={() => dismissAlert(alert.id)}
                              className="px-3 py-1 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-xs font-medium whitespace-nowrap transition-all duration-200"
                            >
                              ✕ Dismiss
                            </button>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
              )}

              {/* Job Change Detections (Companies Hiring) */}
              {bdInsights.jobChangeDetections && bdInsights.jobChangeDetections.length > 0 && (
                <div className="card p-6">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                    <h3 className="text-lg font-semibold tracking-tight">💼 Job Change Detections (Last 3 Months)</h3>
                    {bdSortToggle}
                  </div>
                  <p className="text-sm text-gray-600 mb-4">Companies your candidates have joined - potential BD opportunities</p>

                  <div className="space-y-3">
                    {sortBd(bdInsights.jobChangeDetections, (c) => Number(c.hire_count)).filter((company: any) => !completedCompanies.has(company.company_name)).map((company: any, idx: number) => {
                      const isExpanded = expandedCompanies.has(company.company_name);
                      return (
                        <div key={idx} className="border border-green-200 rounded-xl overflow-hidden bg-green-50 transition-all duration-200 hover:shadow-card-hover">
                          {/* Company Header */}
                          <div
                            className="p-4 cursor-pointer hover:bg-green-100 transition-all duration-200"
                            onClick={() => toggleCompanyExpansion(company.company_name)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <span className="text-2xl transition-transform duration-200">{isExpanded ? '▼' : '▶'}</span>
                                  <div>
                                    <h4 className="font-semibold text-gray-900 text-lg tracking-tight">{company.company_name}</h4>
                                    <p className="text-sm text-gray-600">{company.hire_count} {company.hire_count === 1 ? 'person' : 'people'} joined</p>
                                    {renderBdCompanyMeta(company)}
                                  </div>
                                </div>
                              </div>

                              {/* Action Buttons */}
                              <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                                <button
                                  onClick={() => markCompanyComplete(company.company_name)}
                                  className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs font-medium transition-all duration-200 hover:-translate-y-px"
                                >
                                  ✓ Complete
                                </button>
                                <div className="relative group">
                                  <button className="px-3 py-1 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-xs font-medium transition-all duration-200">
                                    ⏰ Snooze
                                  </button>
                                  <div className="hidden group-hover:block absolute right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-elevated z-10 whitespace-nowrap overflow-hidden">
                                    <button onClick={() => snoozeCompany(company.company_name, 1)} className="block w-full px-4 py-2 text-left text-xs hover:bg-gray-50 transition-colors">1 day</button>
                                    <button onClick={() => snoozeCompany(company.company_name, 3)} className="block w-full px-4 py-2 text-left text-xs hover:bg-gray-50 transition-colors">3 days</button>
                                    <button onClick={() => snoozeCompany(company.company_name, 7)} className="block w-full px-4 py-2 text-left text-xs hover:bg-gray-50 transition-colors">7 days</button>
                                    <button onClick={() => snoozeCompany(company.company_name, 30)} className="block w-full px-4 py-2 text-left text-xs hover:bg-gray-50 transition-colors">30 days</button>
                                  </div>
                                </div>
                                <button
                                  onClick={() => dismissCompany(company.company_name)}
                                  className="px-3 py-1 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-xs font-medium transition-all duration-200"
                                >
                                  ✕ Dismiss
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Expandable Person List */}
                          {isExpanded && (
                            <div className="px-4 py-3 bg-white border-t border-green-200 animate-slide-in-up">
                              <h5 className="text-sm font-semibold text-gray-700 mb-3">People who joined:</h5>
                              <div className="space-y-2">
                                {company.hires.map((hire: any, hireIdx: number) => (
                                  <div key={hireIdx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200/80 transition-all duration-200 hover:shadow-card-hover hover:border-gray-300">
                                    <div className="flex-1">
                                      <p className="font-medium text-gray-900">{hire.candidateName}</p>
                                      <p className="text-sm text-gray-600">{hire.position}</p>
                                      {hire.fromCompany && <p className="text-xs text-amber-700">← Previously at {hire.fromCompany}</p>}
                                      <p className="text-xs text-gray-500">
                                        {hire.startedOn ? <><span className="font-medium text-gray-700">Started {monthLabel(hire.startedOn)}</span> · </> : null}
                                        detected {new Date(hire.detectedAt).toLocaleDateString()}
                                      </p>
                                    </div>
                                    {hire.linkedinUrl && (
                                      <a
                                        href={hire.linkedinUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn-primary text-xs py-1 px-3"
                                      >
                                        View LinkedIn
                                      </a>
                                    )}
                                  </div>
                                ))}
                              </div>
                              {renderBdContacts(company.company_name)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Backfill Detections (Companies Losing People) */}
              {bdInsights.backfillDetections && bdInsights.backfillDetections.length > 0 && (
                <div className="card p-6">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                    <h3 className="text-lg font-semibold tracking-tight">🔄 Backfill Detections (Last 3 Months)</h3>
                    {bdSortToggle}
                  </div>
                  <p className="text-sm text-gray-600 mb-4">Companies your candidates have left - potential backfill opportunities</p>

                  <div className="space-y-3">
                    {sortBd(bdInsights.backfillDetections, (c) => Number(c.departure_count)).filter((company: any) => !completedCompanies.has(company.company_name)).map((company: any, idx: number) => {
                      const isExpanded = expandedCompanies.has(company.company_name);
                      return (
                        <div key={idx} className="border border-red-200 rounded-xl overflow-hidden bg-red-50 transition-all duration-200 hover:shadow-card-hover">
                          {/* Company Header */}
                          <div
                            className="p-4 cursor-pointer hover:bg-red-100 transition-all duration-200"
                            onClick={() => toggleCompanyExpansion(company.company_name)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <span className="text-2xl transition-transform duration-200">{isExpanded ? '▼' : '▶'}</span>
                                  <div>
                                    <h4 className="font-semibold text-gray-900 text-lg tracking-tight">{company.company_name}</h4>
                                    <p className="text-sm text-gray-600">{company.departure_count} {company.departure_count === 1 ? 'person' : 'people'} left</p>
                                    {renderBdCompanyMeta(company)}
                                  </div>
                                </div>
                              </div>

                              {/* Action Buttons */}
                              <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                                <button
                                  onClick={() => markCompanyComplete(company.company_name)}
                                  className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs font-medium transition-all duration-200 hover:-translate-y-px"
                                >
                                  ✓ Complete
                                </button>
                                <div className="relative group">
                                  <button className="px-3 py-1 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-xs font-medium transition-all duration-200">
                                    ⏰ Snooze
                                  </button>
                                  <div className="hidden group-hover:block absolute right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-elevated z-10 whitespace-nowrap overflow-hidden">
                                    <button onClick={() => snoozeCompany(company.company_name, 1)} className="block w-full px-4 py-2 text-left text-xs hover:bg-gray-50 transition-colors">1 day</button>
                                    <button onClick={() => snoozeCompany(company.company_name, 3)} className="block w-full px-4 py-2 text-left text-xs hover:bg-gray-50 transition-colors">3 days</button>
                                    <button onClick={() => snoozeCompany(company.company_name, 7)} className="block w-full px-4 py-2 text-left text-xs hover:bg-gray-50 transition-colors">7 days</button>
                                    <button onClick={() => snoozeCompany(company.company_name, 30)} className="block w-full px-4 py-2 text-left text-xs hover:bg-gray-50 transition-colors">30 days</button>
                                  </div>
                                </div>
                                <button
                                  onClick={() => dismissCompany(company.company_name)}
                                  className="px-3 py-1 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-xs font-medium transition-all duration-200"
                                >
                                  ✕ Dismiss
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Expandable Person List */}
                          {isExpanded && (
                            <div className="px-4 py-3 bg-white border-t border-red-200 animate-slide-in-up">
                              <h5 className="text-sm font-semibold text-gray-700 mb-3">People who left:</h5>
                              <div className="space-y-2">
                                {company.departures.map((departure: any, depIdx: number) => (
                                  <div key={depIdx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200/80 transition-all duration-200 hover:shadow-card-hover hover:border-gray-300">
                                    <div className="flex-1">
                                      <p className="font-medium text-gray-900">{departure.candidateName}</p>
                                      <p className="text-sm text-gray-600">{departure.position}</p>
                                      {departure.movedTo && <p className="text-xs text-green-700">→ Moved to {departure.movedTo}</p>}
                                      <p className="text-xs text-gray-500">
                                        {departure.leftOn ? <><span className="font-medium text-gray-700">Left {monthLabel(departure.leftOn)}</span> · </> : null}
                                        detected {new Date(departure.detectedAt).toLocaleDateString()}
                                      </p>
                                    </div>
                                    {departure.linkedinUrl && (
                                      <a
                                        href={departure.linkedinUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn-primary text-xs py-1 px-3"
                                      >
                                        View LinkedIn
                                      </a>
                                    )}
                                  </div>
                                ))}
                              </div>
                              {renderBdContacts(company.company_name)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Top Hiring Companies */}
              {bdInsights.topHiring.length > 0 && (
                <div className="card p-6">
                  <h3 className="text-lg font-semibold tracking-tight mb-1">🚀 Top Hiring Companies (BD Targets)</h3>
                  <p className="text-sm text-gray-600 mb-4">Last 6 months</p>
                  <div className="space-y-2">
                    {bdInsights.topHiring.map((company, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200/80 transition-all duration-200 hover:shadow-card-hover hover:border-green-300">
                        <div>
                          <p className="font-semibold text-gray-900">{company.company_name}</p>
                          <p className="text-sm text-gray-600">Net movement: {company.net_movement > 0 ? '+' : ''}{company.net_movement}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold tracking-tight text-green-600">{company.candidates_joined}</p>
                          <p className="text-xs text-gray-500">new hires</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Losing Companies */}
              {bdInsights.topAttrition.length > 0 && (
                <div className="card p-6">
                  <h3 className="text-lg font-semibold tracking-tight mb-1">📉 Companies Losing Talent</h3>
                  <p className="text-sm text-gray-600 mb-4">Last 6 months</p>
                  <div className="space-y-2">
                    {bdInsights.topAttrition.map((company, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200/80 transition-all duration-200 hover:shadow-card-hover hover:border-red-300">
                        <div>
                          <p className="font-semibold text-gray-900">{company.company_name}</p>
                          <p className="text-sm text-gray-600">Net movement: {company.net_movement > 0 ? '+' : ''}{company.net_movement}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold tracking-tight text-red-600">{company.candidates_left}</p>
                          <p className="text-xs text-gray-500">departures</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No data state */}
              {(!bdInsights.jobChangeDetections || bdInsights.jobChangeDetections.length === 0) &&
               (!bdInsights.backfillDetections || bdInsights.backfillDetections.length === 0) &&
               bdInsights.topHiring.length === 0 && (
                <div className="card p-6 text-center">
                  <p className="text-gray-500 mb-2">📊 No company movements detected yet</p>
                  <p className="text-sm text-gray-400">
                    Run monitoring to detect job changes and company movements
                  </p>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Settings Modal */}
      <MonitoringSettingsModal
        projectId={projectId}
        projectName={projectName}
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />

      {showReportModal && (
        <MonitoringReport
          projectId={projectId}
          projectName={projectName}
          onClose={() => setShowReportModal(false)}
        />
      )}

      {showIntelReport && (
        <IntelligenceReport
          projectId={projectId}
          projectName={projectName}
          onClose={() => setShowIntelReport(false)}
        />
      )}

      {/* Alert Detail Modal */}
      {showAlertDetailModal && selectedAlert && (
        <div className="modal-backdrop flex items-center justify-center">
          <div className="modal-dialog max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            {/* Modal Header */}
            <div className={`p-4 ${
              selectedAlert.priority === 'critical' ? 'bg-red-100' :
              selectedAlert.priority === 'high' ? 'bg-orange-100' :
              selectedAlert.priority === 'medium' ? 'bg-yellow-100' :
              'bg-blue-100'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{selectedAlert.title}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      selectedAlert.priority === 'critical' ? 'bg-red-200 text-red-900' :
                      selectedAlert.priority === 'high' ? 'bg-orange-200 text-orange-900' :
                      selectedAlert.priority === 'medium' ? 'bg-yellow-200 text-yellow-900' :
                      'bg-blue-200 text-blue-900'
                    }`}>
                      {selectedAlert.priority.toUpperCase()}
                    </span>
                    <span className="text-xs text-gray-600">
                      {new Date(selectedAlert.created_at).toLocaleString()}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setShowAlertDetailModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold transition-colors duration-200"
                >
                  &times;
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Alert Message */}
              <p className="text-gray-700 mb-4 whitespace-pre-line">{selectedAlert.message}</p>

              {/* Change Tags */}
              {getAlertChangeTags(selectedAlert).length > 0 && (
                <div className="mb-4">
                  <div className="flex flex-wrap gap-2">
                    {getAlertChangeTags(selectedAlert).map((tag, idx) => (
                      <span key={idx} className="badge badge-gray">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Detailed Changes */}
              {getDetailedChanges(selectedAlert).length > 0 ? (
                <div className="space-y-4">
                  {getDetailedChanges(selectedAlert).map((category, catIdx) => (
                    <div key={catIdx} className="border border-gray-200/80 rounded-xl overflow-hidden shadow-card">
                      <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                        <h4 className="font-semibold text-gray-800 tracking-tight">{category.category}</h4>
                      </div>
                      <div className="p-4 space-y-3">
                        {category.changes.map((change, changeIdx) => (
                          <div key={changeIdx} className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <div className="text-sm">
                              <span className="font-medium text-gray-600">{change.label}</span>
                            </div>
                            <div className="text-sm space-y-1">
                              {change.oldValue && change.oldValue !== '(none)' && (
                                <div className="flex items-start gap-2">
                                  <span className="text-red-500 font-medium">Before:</span>
                                  <span className="text-gray-600 break-words">{change.oldValue}</span>
                                </div>
                              )}
                              <div className="flex items-start gap-2">
                                <span className="text-green-500 font-medium">After:</span>
                                <span className="text-gray-900 break-words">{change.newValue}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No detailed change information available.</p>
              )}

              {/* Candidate Info */}
              {selectedAlert.candidate_name && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Candidate:</span>{' '}
                    {selectedAlert.candidate_id ? (
                      <button
                        className="text-blue-600 hover:underline font-medium"
                        title="Open candidate panel"
                        onClick={() => setContactPanel({ open: true, entityId: selectedAlert.candidate_id, entityType: 'candidate' })}
                      >
                        {selectedAlert.candidate_name}
                      </button>
                    ) : (
                      selectedAlert.candidate_name
                    )}
                  </p>
                  {selectedAlert.candidate_linkedin_url && (
                    <a
                      href={selectedAlert.candidate_linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      View LinkedIn Profile →
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-between rounded-b-2xl">
              <button
                onClick={() => setShowAlertDetailModal(false)}
                className="btn-ghost"
              >
                Close
              </button>
              <div className="flex gap-2">
                {lemlistConnected && selectedAlert.candidate_id && (
                  <button
                    onClick={() => openLemlistPush(selectedAlert)}
                    className="px-4 py-2 text-white rounded-lg text-sm font-medium transition-all duration-200 hover:-translate-y-px hover:shadow-md"
                    style={{ backgroundColor: '#3355FF' }}
                    title="Push this candidate into a lemlist campaign with signal context"
                  >
                    Push to lemlist
                  </button>
                )}
                <button
                  onClick={() => {
                    markAlertComplete(selectedAlert.id);
                    setShowAlertDetailModal(false);
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-all duration-200 hover:-translate-y-px hover:shadow-md"
                >
                  Mark Complete
                </button>
                <button
                  onClick={() => {
                    dismissAlert(selectedAlert.id);
                    setShowAlertDetailModal(false);
                  }}
                  className="btn-secondary"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* lemlist push picker — choose a campaign for the signal's candidate. */}
      {showLemlistPush && lemlistPushAlert && (
        <div className="modal-backdrop flex items-center justify-center" style={{ zIndex: 60 }}>
          <div className="modal-dialog p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <svg viewBox="0 0 100 100" className="w-9 h-9 rounded-lg" xmlns="http://www.w3.org/2000/svg" aria-label="lemlist">
                  <rect width="100" height="100" rx="24" fill="#2F6BFF" />
                  <g fill="#ffffff">
                    <rect x="32" y="30" width="11" height="40" rx="5.5" />
                    <rect x="43" y="30" width="24" height="11" rx="5.5" />
                    <rect x="43" y="44.5" width="18" height="11" rx="5.5" />
                    <rect x="43" y="59" width="24" height="11" rx="5.5" />
                  </g>
                </svg>
                <h3 className="text-base font-semibold text-gray-800">Push to lemlist</h3>
              </div>
              <button
                onClick={() => setShowLemlistPush(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Adds <strong>{lemlistPushAlert.candidate_name || 'this candidate'}</strong> as a lead, with signal context
              ({'{{signal_type}}, {{new_title}}, {{to_company}}'}…) available as lemlist variables.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Campaign</label>
            <select
              value={selectedPushCampaignId}
              onChange={(e) => setSelectedPushCampaignId(e.target.value)}
              className="input mb-4"
            >
              <option value="">Select a campaign…</option>
              {lemlistCampaigns.map((c: any) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLemlistPush(false)}
                disabled={lemlistPushing}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={pushSignalToLemlist}
                disabled={lemlistPushing || !selectedPushCampaignId}
                className="flex-1 px-4 py-2 text-white rounded-md hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: '#3355FF' }}
              >
                {lemlistPushing ? 'Pushing…' : 'Push to lemlist'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full contact panel — reused from the data table so a contact can be
          enriched (email/phone) and actioned straight from a monitoring signal. */}
      <EntityDetailPanel
        isOpen={contactPanel.open}
        onClose={() => setContactPanel({ open: false, entityId: null, entityType: 'contact' })}
        entityType={contactPanel.entityType}
        entityId={contactPanel.entityId}
        projectId={projectId}
      />
    </div>
  );
};

export default MonitoringDashboard;
